// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import Box from "@mui/material/Box";
import React from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  gridClasses,
  GridCellParams,
  GridTreeNode,
} from "@mui/x-data-grid";
import { alpha, styled } from "@mui/material/styles";
import { Button, Typography, Tooltip, IconButton } from "@mui/material";
import { ExamState } from "../../utils/types";
import { isExamEligibleForExtension } from "../../utils/examUtils";
import StartExamAlertDialog from "../exam/StartExamAlertDialog";
import ExtendExamDialog from "../exam/ExtendExamDialog";
import { setSelectedExam } from "../../slices/examSlice/selectedExam";
import { getRegisteredExams } from "../../slices/examSlice/examHistory";
import { RootState, useAppDispatch, useAppSelector } from "../../slices/store";
import LaunchIcon from "@mui/icons-material/Launch";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import { routes } from "../../config/constant";
import { useNavigate } from "react-router-dom";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover, &.Mui-hovered": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover, &.Mui-hovered": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

/**
 * ExamTable displays a list of exams with actions and status.
 * @param props.examData - Array of exam objects
 * @param props.loading - Loading state
 */
export default function ExamTable(props: {
  examData: readonly any[];
  loading: boolean;
}) {
  const dispatch = useAppDispatch();
  const setExam = (
    params: GridCellParams<any, unknown, unknown, GridTreeNode>
  ) => {
    dispatch(setSelectedExam(params.row));
  };
  let navigate = useNavigate();
  const appStatus = useAppSelector((state: RootState) => state.app);
  const contactState = useAppSelector((state: RootState) => state.contact);
  const [, setExtendDialogOpen] = React.useState(false);
  const [selectedExamForExtension, setSelectedExamForExtension] = React.useState<any>(null);

  const handleExtendExam = (examRow: any) => {
    setSelectedExamForExtension(examRow);
    setExtendDialogOpen(true);
  };

  const handleExtendConfirm = () => {
    // Refresh the exam data to reflect the new expiration date
    if (contactState.response?.id) {
      dispatch(getRegisteredExams(contactState.response.id)).then(() => {
        setExtendDialogOpen(false);
        setSelectedExamForExtension(null);
      });
    } else {
      setExtendDialogOpen(false);
      setSelectedExamForExtension(null);
    }
  };

  const handleExtendCancel = () => {
    setExtendDialogOpen(false);
    setSelectedExamForExtension(null);
  };

  // Removed recentlyExtendedExamIds state and wasRecentlyExtended function.

  const isExamExtendable = (expirationDate: string, examId: string, isExtended?: boolean, examState?: string): boolean => {
    return !isExtended && examState === ExamState.ACTIVE && isExamEligibleForExtension(expirationDate);
  };

  const viewStatus = (params: GridRenderCellParams<any, string>) => {
    // Map states to display values as specified in requirements
    let displayValue;
    switch (params.value) {
      case ExamState.ACTIVE:
        displayValue = "Active Exam";
        break;
      case ExamState.IN_PROGRESS:
        displayValue = "Active Exam";
        break;
      case ExamState.ATTEMPTED:
        displayValue = "Attempted";
        break;
      case ExamState.PASSED:
        displayValue = "Passed";
        break;
      case ExamState.FAILED:
        displayValue = "Failed";
        break;
      case ExamState.EXPIRED:
        displayValue = "Expired";
        break;
      case ExamState.COMPLETED:
        displayValue = "Attempted";
        break;
      case ExamState.REGISTERED:
        displayValue = "Active Exam";
        break;
      default:
        displayValue = "Active Exam";
    }

    if (displayValue === "Passed") {
      return (
        <Button
          variant="outlined"
          onClick={() => {
            navigate(`${routes.certifications}`);
          }}
          endIcon={<LaunchIcon />}
          translate="no"
          data-exam-action="view-certification"
          data-original-state={params.value}
          className="exam-status-button"
          sx={{
            textTransform: "none",
            color: "green",
            px: 1,
            fontWeight: 500,
            fontSize: "0.8rem",
          }}
        >
          {displayValue}
        </Button>
      );
    } else {
      return (
        <Typography variant="body2" ml={1} translate="no" className="exam-status-text">
          {displayValue}
        </Typography>
      );
    }
  };

  const viewActions = (params: GridRenderCellParams<any, string>) => {
  const examState = params.row.state;
  const expirationDate = params.row.expirationDate;

  const isExamResumable =
    !appStatus.backgroundProcess &&
    examState === ExamState.IN_PROGRESS &&
    appStatus.response.isExamResumptionEnabled;

  const canStartExam =
    examState === ExamState.ACTIVE ||
    examState === ExamState.REGISTERED ||
    examState === ExamState.IN_PROGRESS;

  const canExtendExam = isExamExtendable(
    expirationDate,
    params.row.id,
    params.row.isExtended,
    examState
  );

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} translate="no">
      {canStartExam ? (
        <StartExamAlertDialog isResume={isExamResumable} />
      ) : (
        <Tooltip title="Start Exam" arrow>
          <span>
            <IconButton
              disabled
              size="small"
              sx={{
                color: 'secondary.main',
                width: 32,
                height: 32,
                opacity: 0.5,
              }}
            >
              <PlayCircleFilledIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {canExtendExam && (
        <Tooltip title="Extend Exam" arrow>
          <IconButton
            size="small"
            sx={{
              color: 'secondary.main',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'rgba(255, 115, 0, 0.04)',
              },
            }}
            onClick={() => handleExtendExam(params.row)}
          >
            <MoreTimeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

  const viewExamHeader = (params: GridRenderCellParams<any, string>) => {
    return (
      <span translate="no" className="exam-name-text">
        {params.value ? params.value : params.row.examType}
      </span>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "examVisibleType",
      renderHeader: () => <strong translate="no">Certification Name</strong>,
      editable: false,
      flex: 1,
      renderCell: viewExamHeader,
    },
    {
      field: "examPaperType",
      renderHeader: () => <strong translate="no">Type of Exam</strong>,
      minWidth: 200,
      sortable: true,
    },
    {
      field: "expirationDate",
      renderHeader: () => <strong translate="no">Expiration Date</strong>,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "state",
      renderHeader: () => <strong translate="no">Status</strong>,
      sortable: true,
      editable: false,
      minWidth: 150,
      renderCell: viewStatus,
    },
    {
      field: "action",
      renderHeader: () => <strong translate="no">Action</strong>,
      sortable: false,
      editable: false,
      minWidth: 200,
      renderCell: viewActions,
    },
  ];
  const stateMap = new Map<ExamState, any[]>([
    [ExamState.IN_PROGRESS, []],
    [ExamState.ACTIVE, []],
    [ExamState.PASSED, []],
    [ExamState.FAILED, []],
    [ExamState.ATTEMPTED, []],
    [ExamState.EXPIRED, []],
    [ExamState.REGISTERED, []],
    [ExamState.COMPLETED, []],
  ]);
  const sortByState = (data: any[]) => {
    if (data.length > 0) {
      const lists = new Map<ExamState, any[]>(stateMap);
      data.forEach((e) => {
        const list = lists.get(e.state) ?? [];
        list.push(e);
      });
      const res = Array.from(lists.values()).flat();
      return res;
    } else {
      return [];
    }
  };
  return (
    <Box sx={{ width: "100%", marginBottom: 2, mt: 4 }} translate="no" className="exam-table-container">
      <StripedDataGrid
        onCellClick={(params, event) => {
          setExam(params);
        }}
        autoHeight
        rows={sortByState([...props.examData])}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 20, 30]}
        disableRowSelectionOnClick
        loading={props.loading}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        slotProps={{
          baseButton: {
            'translate': 'no' as const,
          } as Record<string, any>,
        }}
      />
      {selectedExamForExtension && (
        <ExtendExamDialog
          examType={selectedExamForExtension.examPaperType || ''}
          examName={selectedExamForExtension.examVisibleType || selectedExamForExtension.examType || ''}
          examRecordId={selectedExamForExtension.id}
          onExtend={() => {
            handleExtendConfirm();
          }}
          onCancel={handleExtendCancel}
        />
      )}
    </Box>
  );
}
